/**
 * RedactIt product-analytics events.
 *
 * Thin typed wrappers over the shared `sendEvent` (a Datadog RUM custom
 * action). Every helper sets `object_type`/`object_id`; `app_id`, `lane`,
 * `app_version`, `is_internal`, `tenant_id`, `tenant` and `roles` are
 * auto-stamped by the shared telemetry module — never pass them.
 * See `shared/telemetry/EVENT_CATALOG.md`.
 *
 * PII rule: props carry ids / types / counts / enums only — never person
 * names, email addresses, filenames, case names, redaction bounds/content, or
 * document content.
 */
import { sendEvent } from '@darwin-launchpad/legacy-shared/telemetry';

export function trackCaseCreated(caseId: string, args?: { notes_length?: number }): void {
  sendEvent('case_created', {
    object_type: 'case',
    object_id: caseId,
    ...args,
  });
}

export function trackDocumentUploaded(documentId: string): void {
  sendEvent('document_uploaded', {
    object_type: 'document',
    object_id: documentId,
  });
}

export function trackRedactionJobStarted(
  documentId: string,
  args: { flow: 'redaction'; enable_ocr: boolean; ocr_engine?: string },
): void {
  sendEvent('redaction_job_started', {
    object_type: 'redaction_job',
    object_id: documentId,
    ...args,
  });
}

export function trackRedactionSuggestionsApproved(
  jobId: string,
  args?: { approved_count?: number },
): void {
  sendEvent('redaction_suggestions_approved', {
    object_type: 'redaction_job',
    object_id: jobId,
    ...args,
  });
}

export function trackRedactionSuggestionsRejected(
  jobId: string,
  args?: { rejected_count?: number },
): void {
  sendEvent('redaction_suggestions_rejected', {
    object_type: 'redaction_job',
    object_id: jobId,
    ...args,
  });
}

export function trackManualRedactionAdded(
  documentId: string,
  args: { kind: 'pdf' | 'text'; page?: number },
): void {
  sendEvent('manual_redaction_added', {
    object_type: 'redaction',
    object_id: documentId,
    ...args,
  });
}

export function trackPageBlackoutToggled(
  jobId: string,
  args?: { page?: number; is_removing?: boolean },
): void {
  sendEvent('page_blackout_toggled', {
    object_type: 'redaction',
    object_id: jobId,
    ...args,
  });
}

export function trackDocumentFinalized(
  documentId: string,
  args?: { approved_count?: number; status?: string },
): void {
  sendEvent('document_finalized', {
    object_type: 'document',
    object_id: documentId,
    ...args,
  });
}

export function trackDocumentReprocessed(documentId: string, args: { mode: string }): void {
  sendEvent('document_reprocessed', {
    object_type: 'document',
    object_id: documentId,
    ...args,
  });
}

export function trackDocumentProcessingRetried(documentId: string): void {
  sendEvent('document_processing_retried', {
    object_type: 'document',
    object_id: documentId,
  });
}

export function trackRedactedDocumentDownloaded(
  documentId: string,
  args?: { version?: string; format?: string },
): void {
  sendEvent('redacted_document_downloaded', {
    object_type: 'document',
    object_id: documentId,
    ...args,
  });
}

export function trackCaseDownloadedZip(caseId: string): void {
  sendEvent('case_downloaded_zip', {
    object_type: 'case',
    object_id: caseId,
  });
}

export function trackCaseExportedMegaPdf(caseId: string): void {
  sendEvent('case_exported_mega_pdf', {
    object_type: 'case',
    object_id: caseId,
  });
}

export function trackRedactedDocumentPrinted(documentId: string): void {
  sendEvent('redacted_document_printed', {
    object_type: 'document',
    object_id: documentId,
  });
}

// --- NextRequest import (sc-11766) ------------------------------------------
// Counts and enums only: never file names, request text or who is signed in.

export type NextRequestExtensionState = 'checking' | 'not_installed' | 'signed_out' | 'no_access' | 'ready';
export type NextRequestSessionState = 'signed_in' | 'signed_out' | 'unknown' | 'none';

/** Import files clicked on the Data collection step. */
export function trackNextRequestImportClicked(
  caseId: string,
  args: { selected: number; released: number; staff_only: number; extension_state: NextRequestExtensionState; nextrequest_session: NextRequestSessionState },
): void {
  sendEvent('nextrequest_import_clicked', { object_type: 'case', object_id: caseId, ...args });
}

/** The "only released files" popover by Import files opened; `state` = why. */
export function trackNextRequestPopoverShown(
  caseId: string,
  args: { state: 'not_installed' | 'extension_signed_out' | 'no_access' | 'nextrequest_signed_out' | 'nextrequest_unknown' },
): void {
  sendEvent('nextrequest_popover_shown', { object_type: 'case', object_id: caseId, ...args });
}

/** What the user did in that popover. */
export function trackNextRequestPopoverAction(
  caseId: string,
  args: { action: 'import_released' | 'import_all' | 'open_nextrequest' | 'sign_in' | 'dismissed' },
): void {
  sendEvent('nextrequest_popover_action', { object_type: 'case', object_id: caseId, ...args });
}

/** An import finished: through the extension or on the server, with its counts. */
export function trackNextRequestImportFinished(
  caseId: string,
  args: { route: 'extension' | 'server'; ok: number; failed: number; skipped: number },
): void {
  sendEvent('nextrequest_import_finished', { object_type: 'case', object_id: caseId, ...args });
}
